import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Money } from '../../../core/model/money';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { CreditInstituteService } from '../../../mortgage/service/credit-institute.service';
import { BrfMortgagePrescription } from '../../model/brf-mortgage-prescription';
import { Dwelling } from '../../model/dwelling';
import { OtherRentalObject } from '../../model/other-rental-object';
import { Parking } from '../../model/parking';
import { Premises } from '../../model/premises';

@Component({
    selector: 'app-rental-object-mortgage-prescriptions',
    templateUrl: './rental-object-mortgage-prescriptions.component.html',
    styleUrls: ['./rental-object-mortgage-prescriptions.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RentalObjectMortgagePrescriptionsComponent implements OnInit, OnDestroy {
  constructor(
    private _fb: FormBuilder,
    private _cf: FormControlExFactory,
    readonly creditInstituteService: CreditInstituteService
  ) {}

  private _forms: FormArray;
  @Input() set forms(value: AbstractControl) {
    this._forms = value as FormArray;
  }
  get forms(): FormArray {
    return this._forms;
  }
  @Input() mortgagePrescription: BrfMortgagePrescription[];
  @Input() rentalObject: Dwelling | Premises | Parking | OtherRentalObject;
  private _subscription = new Subscription();

  addMortgage() {
    this.forms.controls.push(
      this.createForm(
        new BrfMortgagePrescription({
          rentalObjectId: this.rentalObject.id,
          creditInstituteId: null,
          leaseAgreementId: null,
          mortgageOrder: null,
          mortgageNumber: null,
          amount: new Money(this.rentalObject.currencyCode, 0),
        })
      )
    );

    this.forms.markAsDirty();
  }

  delete(mortgage: FormGroup) {
    this.forms.controls.splice(this.forms.controls.indexOf(mortgage), 1);
    this.forms.markAsDirty();
  }

  ngOnDestroy(): void {
    if (this._subscription) this._subscription.unsubscribe();
  }

  initForm() {
    let mortgages = this.rentalObject ? this.rentalObject.brfMortgagePrescriptions : this.mortgagePrescription;
    for (let mortgage of mortgages.sort(
      (a, b) =>
        new Date(a.fromDate.year, a.fromDate.month, a.fromDate.day).getTime() -
        new Date(b.fromDate.year, b.fromDate.month, b.fromDate.day).getTime()
    )) {
      let group = this.createForm(mortgage);
      this.forms.controls.push(group);
    }
  }

  private createForm(mortgage: BrfMortgagePrescription): FormGroup {
    let group = this._fb.group({
      id: this._cf.createHidden(mortgage.id),
      rentalObjectId: this._cf.createHidden(mortgage.rentalObjectId),
      creditInstituteId: this._cf.createMandatory('MORTGAGE.CREDIT_INSTITUTE', mortgage.creditInstituteId),
      mortgageOrder: this._cf.createMandatory('COOPERATIVE_HOUSING.MORTGAGE_ORDER', mortgage.mortgageOrder),
      mortgageNumber: this._cf.create('COOPERATIVE_HOUSING.MORTGAGE_PRESCRIPTION_NUMBER', mortgage.mortgageNumber),
      fromDate: this._cf.createMandatory('COOPERATIVE_HOUSING.MORTGAGE_FROM', mortgage.fromDate),
      toDate: this._cf.create('COOPERATIVE_HOUSING.MORTGAGE_TO', mortgage.toDate),
      amount: this._cf.createMandatory('COOPERATIVE_HOUSING.MORTGAGE_AMOUNT', mortgage.amount),
    });

    this._subscription.add(group.controls['amount'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    this._subscription.add(
      group.controls['mortgageNumber'].valueChanges.subscribe(val => {
        this.forms.markAsDirty();
      })
    );
    this._subscription.add(
      group.controls['mortgageOrder'].valueChanges.subscribe(val => {
        this.forms.markAsDirty();
      })
    );
    this._subscription.add(
      group.controls['fromDate'].valueChanges.subscribe(val => {
        this.forms.markAsDirty();
      })
    );
    this._subscription.add(
      group.controls['toDate'].valueChanges.subscribe(val => {
        this.forms.markAsDirty();
      })
    );
    this._subscription.add(
      group.controls['creditInstituteId'].valueChanges.subscribe(val => {
        this.forms.markAsDirty();
      })
    );

    return group;
  }
  ngOnInit(): void {
    this.initForm();
  }
}
